import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import { usePrevious } from 'hooks';
import { Autocomplete } from 'components/FormFields';
import { PricingActions } from 'state/modules/pricing';
import { truncateKeywordSpaces } from 'utils/utils';

const TaxRulesetsSelect = ({
  label,
  variant,
  field: { name, value },
  style,
  id,
  form: { errors, touched, setFieldValue },
  disabled,
  datacy,
  customParams,
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });
  const {
    taxRulesets: { data },
    fetchingTaxRulesets,
  } = useSelector((state) => state.pricing);
  const prevvalue = usePrevious(value);
  const prev = usePrevious(customParams);
  /**
   * Fetch tax rulesets list
   * @param {*} params
   */
  const fetchTaxRulesets = useCallback(
    debounce((params = {}) => {
      const newParams = { ...params };
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
        delete newParams?.rules_set_id;
      }
      if (!newParams?.rules_set_id) {
        delete newParams?.rules_set_id;
      }
      dispatch(PricingActions.fetchTaxRulesets(newParams));
    }, 1000),
    []
  );

  useEffect(() => {
    if (!value) return;
    fetchTaxRulesets({ ...params });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (prev && customParams && JSON.stringify(prev) !== JSON.stringify(customParams)) {
      fetchTaxRulesets({ ...params, ...customParams });
    }
  }, [customParams]); // eslint-disable-line

  useEffect(() => {
    if (prevvalue !== value || !defaultValue) {
      const defaultVal = data.find((el) => el?.id === parseInt(value));
      if (defaultVal && defaultVal?.id !== defaultValue?.id) {
        setDefaultValue(defaultVal);
      } else {
        setDefaultValue(null);
      }
    }
  }, [value, data]); // eslint-disable-line

  const handleFetchTaxRulesets = () => {
    if (data.length === 0 && !fetchingTaxRulesets && !params?.keyword) {
      const newParams = { ...params };
      if (!newParams?.rules_set_id) {
        delete newParams?.rules_set_id;
      }
      dispatch(PricingActions.fetchTaxRulesets(newParams));
    }
  };

  return (
    <Autocomplete
      name={name}
      style={style}
      label={label}
      margin="none"
      options={data}
      onHighlightChange={handleFetchTaxRulesets}
      dataCy={otherProps['data-cy'] || name}
      fullwidth="true"
      variant={variant}
      disabled={disabled}
      clearOnBlur={false}
      value={defaultValue}
      id="TaxRulesetsSelect"
      loading={fetchingTaxRulesets}
      getOptionLabel={(option) => `${option?.name || option?.display_name || ''}`}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={(option, value) => option?.id === value?.id}
      onChange={(ev, val) => setFieldValue(name, val?.id, val || '')}
      onInputChange={(ev, val) => fetchTaxRulesets({ ...params, keyword: val })}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
    />
  );
};

TaxRulesetsSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  style: PropTypes.object,
  customParams: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

TaxRulesetsSelect.defaultProps = {
  required: true,
  disabled: false,
  form: {
    errors: {},
    touched: {},
  },
  style: {},
  customParams: {},
  label: 'Select...',
  variant: 'standard',
  datacy: 'tax-rulesets-select',
};

export default TaxRulesetsSelect;
